import { Catalogue, CatalogueQuestion, QuestionSource, QuestionType } from "../types/catalogue"
import {
  createEmptySpec,
  newTabSpecId,
  RowItem,
  SummaryChoice,
  TableKind,
  TableSpec,
  TabSpecDefaults,
  TabSpecDocument,
} from "../types/tabSpecDocument"
import { isYesNoField } from "./dataVariables"
import { boxMembers, defaultScores, isNps, isScale, labelsInRange } from "./scale"
import { canSummarise, defaultSlices, summaryFits } from "./summaries"

/**
 * The table kinds every question of a type can be shown as; the first is the
 * default for a non-scale. A table of yes or no fields from the data is
 * multiple choice by default and may be shown as single choice (the script
 * and the figures are the same, only the words differ). Whether it may also
 * be a scale depends on the variable, not the type (a lone yes or no field
 * never is): `kindsForQuestion` says so.
 */
export const kindsFor = (type: QuestionType, source?: QuestionSource): TableKind[] => {
  if (source === "data" && (type === "select-many" || type === "grid-many"))
    return ["multi", "categorical"]
  switch (type) {
    case "select-one":
    case "range":
      return ["categorical", "scale"]
    case "select-many":
      return ["multi"]
    case "number":
      return ["numeric"]
    case "text":
      return ["text"]
    case "grid-one":
    case "grid-range":
      return ["grid-summary", "categorical", "scale"]
    case "grid-many":
      return ["multi"]
    case "grid-number":
      return ["grid-summary"]
  }
}

/**
 * The table kinds `q` can be shown as. A table of yes or no fields from the
 * data (not a lone one) may also be a scale: unless the codebook says its
 * fields exclude each other (`answers` "single"), when it runs the engine
 * checks that every respondent has exactly one marked, and the mean is left
 * out when not. One the codebook calls "multi" is never a scale. A lone yes
 * or no field always has the rows Yes and No, so it is never a scale. Any
 * question with more than one instance (a grid or a loop, not text) may
 * also have grid summaries.
 */
export const kindsForQuestion = (q: CatalogueQuestion): TableKind[] => {
  const kinds = kindsFor(q.type, q.source)
  const choices = q.source === "data" && (q.type === "select-many" || q.type === "grid-many")
  const all: TableKind[] =
    choices && !isYesNoField(q) && q.answers !== "multi" ? [...kinds, "scale"] : kinds
  return canSummarise(q) && !all.includes("grid-summary") ? [...all, "grid-summary"] : all
}

/** The kind of the table for one instance of `q` (not the grid summary). */
export const kindFor = (q: CatalogueQuestion): TableKind => {
  switch (q.type) {
    case "select-one":
    case "range":
    case "grid-one":
    case "grid-range":
      return isScale(q) ? "scale" : "categorical"
    case "select-many":
    case "grid-many":
      return "multi"
    case "number":
      return "numeric"
    case "grid-number":
      return "grid-summary"
    case "text":
      return "text"
  }
}

export const defaultRows = (
  kind: TableKind,
  q: CatalogueQuestion,
  d: TabSpecDefaults,
  scores: Record<string, number> | null
): RowItem[] => {
  if (kind === "numeric" || kind === "text" || kind === "grid-summary") return []
  const rows: RowItem[] = q.options.map((o) => ({ kind: "option", label: o.label }))
  if (kind !== "scale" || !scores) return rows
  const count = Object.keys(scores).length
  if (isNps(scores)) {
    rows.push(
      { kind: "net", name: "Promoters", members: labelsInRange(q, scores, 9, 10) },
      { kind: "net", name: "Passives", members: labelsInRange(q, scores, 7, 8) },
      { kind: "net", name: "Detractors", members: labelsInRange(q, scores, 0, 6) },
      { kind: "difference", name: "NPS", plus: "Promoters", minus: "Detractors" }
    )
  } else {
    if (d.scale.topBox > 0 && count >= d.scale.topBox * 2)
      rows.push({
        kind: "net",
        name: `Top ${d.scale.topBox}`,
        members: boxMembers(q, scores, d.scale.topBox as 2 | 3, "top"),
      })
    if (d.scale.bottomBox > 0 && count >= d.scale.bottomBox * 2)
      rows.push({
        kind: "net",
        name: `Bottom ${d.scale.bottomBox}`,
        members: boxMembers(q, scores, d.scale.bottomBox as 2 | 3, "bottom"),
      })
  }
  if (d.scale.mean) rows.push({ kind: "mean" })
  return rows
}

const GRID_TYPES: QuestionType[] = ["grid-one", "grid-range", "grid-many", "grid-number"]

/**
 * A new summary of `q` (`make` builds it): one table, or for a question of
 * several dimensions with grid rows one per combination of the values of
 * its other dimensions, its rows the grid rows (`defaultSlices`): a summary
 * per column, or per loop iteration.
 */
const slicedTables = (q: CatalogueQuestion, make: () => TableSpec): TableSpec[] => {
  const slices = defaultSlices(q)
  return slices.length > 0 ? slices.map((slice) => ({ ...make(), slice })) : [make()]
}

/**
 * The summaries `choices` give `q`, each a table of its own (`summary`
 * set), in the order of `choices`: those that suit it (`summaryFits`, by
 * its default scores), sliced as every new summary is (`slicedTables`).
 * `make` builds each table.
 */
export const summaryTablesFor = (
  q: CatalogueQuestion,
  choices: SummaryChoice[],
  make: () => TableSpec
): TableSpec[] => {
  const scores = defaultScores(q)
  return choices
    .filter((c) => summaryFits(c, q, scores))
    .flatMap((summary) => slicedTables(q, () => ({ ...make(), summary })))
}

/**
 * The tables of a grid or a loop with the summaries `summaries` (null: the
 * one summary of the defaults' `grid.summary`, as before summaries had their
 * own, with no `summary` stored) and, with `perRow`, a table per instance
 * where the question has them. Every new summary of a question of several
 * dimensions is a table per value of its dimensions other than the grid
 * rows (`slicedTables`), whichever way it is chosen. A number grid has no
 * table per cell.
 */
export const gridTablesFor = (
  q: CatalogueQuestion,
  d: TabSpecDefaults,
  choice: { summaries: SummaryChoice[] | null; perRow: boolean },
  makeId: () => string = () => newTabSpecId("table")
): TableSpec[] => {
  const make = (instance: string | null, kind: TableKind): TableSpec => ({
    id: makeId(),
    question: q.label,
    instance,
    kind,
    origin: "default",
    include: kind !== "text" && !(q.hidden && !d.includeHidden),
    filter: [],
    rows: null,
    scores: null,
  })
  const perInstance = (): TableSpec[] =>
    choice.perRow ? q.instances.map((i) => make(i.id === "" ? null : i.id, kindFor(q))) : []
  const numberGrid = q.type === "grid-number"
  const summary = (): TableSpec => make(null, "grid-summary")
  if (choice.summaries === null) {
    if (numberGrid) return slicedTables(q, summary)
    // Only a grid of answers had a summary of its own.
    const legacy =
      GRID_TYPES.includes(q.type) && q.type !== "grid-many" && d.grid.summary !== "none"
        ? slicedTables(q, summary)
        : []
    return [...legacy, ...perInstance()]
  }
  const summaries = summaryTablesFor(q, choice.summaries, summary)
  return numberGrid ? summaries : [...summaries, ...perInstance()]
}

/**
 * The default tables of `q`. A grid of the data whose rows are sibling
 * containers (`q.siblings`) of categories is one table, each sibling's rows
 * in a block of their own (`siblingLayout` "stacked"): single choice, or
 * multiple choice when the codebook says several may be marked. The table
 * of one sibling alone is its own variable's. Any other grid gets its
 * summaries (`gridTablesFor`): one per entry of the defaults' `grid.summaries`,
 * or, without that list, the one summary of `grid.summary`, as before; each
 * a table per column or loop iteration for a grid of several dimensions.
 */
export const defaultTablesFor = (
  q: CatalogueQuestion,
  d: TabSpecDefaults,
  makeId: () => string = () => newTabSpecId("table")
): TableSpec[] => {
  const make = (instance: string | null, kind: TableKind): TableSpec => ({
    id: makeId(),
    question: q.label,
    instance,
    kind,
    origin: "default",
    include: kind !== "text" && !(q.hidden && !d.includeHidden),
    filter: [],
    rows: null,
    scores: null,
  })
  const perInstance = (): TableSpec[] =>
    q.instances.map((i) => make(i.id === "" ? null : i.id, kindFor(q)))

  if (!GRID_TYPES.includes(q.type)) return perInstance()
  if (q.type === "grid-many" && q.siblings)
    return [
      {
        ...make(null, q.answers === "multi" ? "multi" : "categorical"),
        siblingLayout: "stacked",
      },
    ]
  if (q.type === "grid-many") return perInstance()
  // A grid's summaries: the defaults' list, one table each, or (without a
  // list) the one summary of `grid.summary`, as before.
  const tables = gridTablesFor(q, d, { summaries: d.grid.summaries ?? null, perRow: d.grid.perRow }, makeId)
  // A number grid keeps a table: the mean when none of the defaults' summaries suits it.
  return q.type === "grid-number" && tables.length === 0
    ? gridTablesFor(q, d, { summaries: [{ kind: "mean" }], perRow: false }, makeId)
    : tables
}

/**
 * The tables "Add table, From the data" and "Build tables from the data"
 * make: the defaults of the kind, origin "added", source "data". A table of
 * yes or no fields the codebook calls "single" is single choice whatever
 * `as` says. A grid from sibling containers gives its one table, as
 * `defaultTablesFor` does (`as` "categorical" makes it single choice).
 */
export const dataTablesFor = (
  q: CatalogueQuestion,
  d: TabSpecDefaults,
  makeId?: () => string,
  /** "categorical": a table of yes or no fields (not a lone one) is made single choice. */
  as: "multi" | "categorical" = "multi"
): TableSpec[] => {
  const single = (as === "categorical" || q.answers === "single") && !isYesNoField(q)
  return defaultTablesFor(q, d, makeId).map(
    (t): TableSpec => ({
      ...t,
      ...(single && t.kind === "multi" ? { kind: "categorical" as const } : {}),
      origin: "added",
      source: "data",
    })
  )
}

/** A new spec with the default tables of every question, in questionnaire order. */
export const createSpec = (
  name: string,
  user: string,
  catalogue: Catalogue,
  makeId?: () => string
): TabSpecDocument => {
  const spec = createEmptySpec(name, user)
  return {
    ...spec,
    tables: catalogue.questions.flatMap((q) => defaultTablesFor(q, spec.defaults, makeId)),
  }
}
